import axios from 'axios';
import t from 'react-translate';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { MentoringProgramActionItem } from 'redux/schemas/models/mentoring-program-action-items';
import { ApplyChangesOption } from 'athena/components/mentoring-program/home/participant/session-edit-mode/apply-changes-modal';
import { PulseCheckResponse } from 'redux/schemas/models/mentoring-program-sessions';
import { getMentoringProgramEnrollment } from './mentoring-program-enrollments';

type CreateMentoringProgramSessionParams = {
  programId: number;
  enrollmentId: number;
  mentorshipProgramConnectionId: number;
  title: string;
  details?: string;
  scheduledDate: string;
  duration?: number;
  location?: string;
  cadence?: string;
  endsOn?: string;
  joinUrl?: string;
  sessionType?: string;
  timezone?: string;
  createMeetingPlan?: boolean;
};

enum MentoringProgramSessionType {
  SINGLE = 'mentorship_program_session',
  PLAN = 'mentorship_program_session_plan',
}

export const createMentoringProgramSession = createAsyncThunk(
  'CREATE_MENTORING_PROGRAM_SESSION',
  async ({
    programId,
    mentorshipProgramConnectionId,
    title,
    details,
    scheduledDate,
    duration,
    location,
    cadence,
    endsOn,
    joinUrl,
    sessionType,
    timezone,
    createMeetingPlan,
    enrollmentId,
  }: CreateMentoringProgramSessionParams, thunkAPI) => {
    const key = createMeetingPlan ? MentoringProgramSessionType.PLAN : MentoringProgramSessionType.SINGLE;
    const extraParams = createMeetingPlan ? {
      cadence,
      end_date: endsOn,
      start_date: scheduledDate,
    } : {
      scheduled_date: scheduledDate,
    };
    const response = await axios.post(
      `/mentorship_programs/${programId}/sessions`,
      {
        [key]: {
          title,
          details: details || '',
          duration,
          location,
          join_url: joinUrl,
          timezone,
          ...extraParams,
          external_meeting_type: sessionType,
        },
        mentorship_program_connection_id: mentorshipProgramConnectionId,
      },
    );
    const message = createMeetingPlan
      ? t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.CREATE_MEETING_PLAN_SUCCESS(response.data.result.length ?? 0)
      : t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.ADD_SUCCESS_MESSAGE();

    thunkAPI.dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.FORM.SUCCESS_BANG(),
      message,
    }));

    thunkAPI.dispatch(getMentoringProgramEnrollment({ programId, enrollmentId }));

    thunkAPI.dispatch(resetMentoringProgramNextSteps({ resetNextSteps: true }));

    return response.data.result;
  },
);

type GetMentoringProgramSession = {
  programId: number;
  sessionId: number;
};

export const getSingleMentoringProgramSession = createAsyncThunk(
  'GET_MENTORING_PROGRAM_SESSION',
  async ({ programId, sessionId }: GetMentoringProgramSession) => {
    const response = await axios.get(`/mentorship_programs/${programId}/sessions/${sessionId}.json`);

    return response.data.result;
  },
);

export const getMentoringProgramSessions = createAsyncThunk(
  'GET_MENTORING_PROGRAM_SESSIONS',
  async ({ programId, connectionId, page, pageSize, filter, sortByCreated }: any) => {
    // TODO: talk with backend about changing these names so that we can simplify the types
    const params: any = {
      page,
      page_size: pageSize,
      status: filter,
    };

    if (filter === 'past') {
      params.scheduled_date_desc = true;
    } else {
      params.scheduled_date_asc = true;
    }

    if (sortByCreated === 'asc') {
      params.created_at_asc = true;
    }
    if (sortByCreated === 'desc') {
      params.created_at_desc = true;
    }

    params.mentorship_program_connection_id = connectionId;

    const response = await axios.get(`/mentorship_programs/${programId}/sessions`, { params });

    return response.data.result;
  },
);

export const updateMentoringProgramSession = createAsyncThunk(
  'UPDATE_MENTORING_PROGRAM_SESSIONS',
  async ({
    programId,
    sessionId,
    title,
    details,
    scheduledDate,
    duration,
    location,
    applyChangesOption,
    cadence,
    endsOn,
    joinUrl,
    sessionType,
    timezone,
    showAlertMessage = true,
    enrollmentId,
  }: any, thunkAPI) => {
    let updateRemainingSessions;
    let meetingPlan = {};
    let message = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.UPDATE_SUCCESS_MESSAGE();
    if (applyChangesOption === ApplyChangesOption.REMAINING_SESSIONS) {
      updateRemainingSessions = true;
      meetingPlan = {
        [MentoringProgramSessionType.PLAN]: {
          cadence,
          end_date: endsOn,
          start_date: scheduledDate,
          location,
          duration,
          title,
        },
      };
      message = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.UPDATE_MEETING_PLAN_SUCCESS();
    } else if (applyChangesOption === ApplyChangesOption.SINGLE_SESSION) {
      updateRemainingSessions = false;
    }

    const response = await axios.put(`/mentorship_programs/${programId}/sessions/${sessionId}.json`, {
      [MentoringProgramSessionType.SINGLE]: {
        id: sessionId,
        title,
        details: details || undefined,
        duration,
        location,
        join_url: joinUrl,
        integration_type: sessionType,
        timezone,
        scheduled_date: scheduledDate,
        external_meeting_type: sessionType,
      },
      ...meetingPlan,
      update_remaining_sessions: updateRemainingSessions || undefined,
    });

    if (showAlertMessage) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS_BANG(),
        message,
      }));
    }
    thunkAPI.dispatch(getMentoringProgramEnrollment({ programId, enrollmentId }));

    return response.data.result;
  },
);

export const deleteMentoringProgramSession = createAsyncThunk(
  'DELETE_MENTORING_PROGRAM_SESSION',
  async ({ programId, sessionId, enrollmentId }: any, thunkAPI) => {
    const response = await axios.delete(`/mentorship_programs/${programId}/sessions/${sessionId}.json`);

    thunkAPI.dispatch(getMentoringProgramEnrollment({ programId, enrollmentId }));

    return response.data.result;
  },
);

export const getMentoringProgramSessionsCounts = createAsyncThunk(
  'GET_MENTORING_PROGRAM_SESSIONS_COUNTS',
  async ({ programId, connectionId }: any) => {
    const params = {
      mentorship_program_connection_id: connectionId,
    };

    const response = await axios.get(`/mentorship_programs/${programId}/sessions/count.json`, { params });

    return response.data.result;
  },
);

type SaveSessionPulseCheck = {
  programId: number;
  sessionId: number;
  pulseCheckResponses: PulseCheckResponse[];
};

export const saveMentoringProgramSessionPulseCheck = createAsyncThunk(
  'SAVE_MENTORING_PROGRAM_SESSION_PULSE_CHECK',
  async ({ programId, sessionId, pulseCheckResponses }: SaveSessionPulseCheck) => {
    const response = await axios.post(`/mentorship_programs/${programId}/sessions/${sessionId}/pulse_check`, { pulseCheckResponses });

    return response.data.result;
  },
);

export const resetMentoringProgramSessionsList = createAction<{
  resetList: boolean;
}>('RESET_MENTORING_PROGRAMS_SESSIONS_LIST');

export const addCreatedSessionActionItem = createAction<{
  sessionId: number;
  actionItem: MentoringProgramActionItem;
}>('ADD_CREATED_MENTORING_PROGRAM_SESSION_ACTION_ITEM');

export const updateModifiedSessionActionItem = createAction<{
  sessionId: number;
  actionItem: MentoringProgramActionItem;
}>('UPDATE_MODIFIED_MENTORING_PROGRAM_SESSION_ACTION_ITEM');

export const removeDeletedSessionActionItem = createAction<{
  sessionId: number;
  actionItemId: number;
}>('REMOVE_DELETED_MENTORING_PROGRAM_SESSION_ACTION_ITEM');

type FetchMeetingPlanParams = {
  programId: number;
  mentorshipProgramConnectionId: number;
};

export const fetchMeetingPlan = createAsyncThunk(
  'FETCH_MEETING_PLAN',
  async ({ mentorshipProgramConnectionId, programId }: FetchMeetingPlanParams) => {
    const response = await axios.get(`/mentorship_programs/${programId}/session_plans?mentorship_program_connection_id=${mentorshipProgramConnectionId}`);
    return response.data.result;
  },
);

export const resetMentoringProgramNextSteps = createAction<{
  resetNextSteps: boolean;
}>('RESET_MENTORING_PROGRAMS_NEXT_STEPS');
